import { useEffect, useState } from 'react';
import { Inbox, Loader2, CheckCircle2, HelpCircle, MinusCircle, AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { api } from '../lib/api';
import { linkifyMail } from '../lib/linkifyMail';

type MailMessage = {
  id: number;
  accountId: number;
  fromAddress: string | null;
  subject: string | null;
  sentAt: string | null;
  detectedType: string | null;
  matchedCustomerId: number | null;
  confidence: number | null;
  status: string;
  error: string | null;
  documentStatus: string | null;
  vorgangsnummer: string | null;
  vorgangId: number | null;
  createdAt: string;
};

type PollResult = {
  accountId: number;
  fetched: number;
  zugeordnet: number;
  nichtZugeordnet: number;
  irrelevant: number;
  fehler: number;
};

const TYPE_LABEL: Record<string, string> = {
  antragsbestaetigung: 'Antragsbestätigung',
  erlaubnis: 'Erlaubnis Beratungsbeginn',
  zuwendungsbescheid: 'Zuwendungsbescheid',
  verwendungsnachweis_link: 'Verwendungsnachweis-Link',
  unterlagen_ruecklauf: 'Rücklauf vom Kunden',
  sonstige_bafa: 'Sonstige BAFA',
  irrelevant: 'Irrelevant',
};

interface Props {
  darkMode: boolean;
  cardClasses: string;
}

export function MailMonitorInbox({ darkMode, cardClasses }: Props) {
  const [rows, setRows] = useState<MailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState('');
  // Original-Mail: Volltext wird erst auf Knopfdruck geladen und dann gemerkt.
  const [openMail, setOpenMail] = useState<number | null>(null);
  const [bodies, setBodies] = useState<Record<number, string | null>>({});
  const [bodyLoading, setBodyLoading] = useState<number | null>(null);

  const toggleMail = async (id: number) => {
    if (openMail === id) { setOpenMail(null); return; }
    setOpenMail(id);
    if (bodies[id] !== undefined) return;
    setBodyLoading(id);
    try {
      const res = await api.get<{ bodyText: string | null }>(`/api/mail-monitor/messages/${id}/body`);
      setBodies((b) => ({ ...b, [id]: res.bodyText }));
    } catch {
      setBodies((b) => ({ ...b, [id]: null }));
    } finally {
      setBodyLoading(null);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await api.get<MailMessage[]>('/api/mail-monitor/messages'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!pollMsg) return;
    const t = setTimeout(() => setPollMsg(''), 6000);
    return () => clearTimeout(t);
  }, [pollMsg]);

  const handlePoll = async () => {
    setPolling(true);
    setError('');
    setPollMsg('');
    try {
      const res = await api.post<PollResult[]>('/api/mail-monitor/poll');
      const fetched = res.reduce((n, r) => n + r.fetched, 0);
      const zugeordnet = res.reduce((n, r) => n + r.zugeordnet, 0);
      const fehler = res.reduce((n, r) => n + r.fehler, 0);
      setPollMsg(
        fetched === 0
          ? 'Prüfung abgeschlossen — keine neuen Mails.'
          : `Prüfung abgeschlossen — ${fetched} neue Mail(s) verarbeitet` +
              (zugeordnet ? `, ${zugeordnet} zugeordnet` : '') +
              (fehler ? `, ${fehler} Fehler` : '') +
              '.',
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Prüfung fehlgeschlagen');
    } finally {
      setPolling(false);
    }
  };

  const statusBadge = (s: string) => {
    const map: Record<string, { Icon: typeof CheckCircle2; cls: string; label: string }> = {
      zugeordnet: { Icon: CheckCircle2, cls: 'text-green-700 dark:text-green-400', label: 'Zugeordnet' },
      nicht_zugeordnet: { Icon: HelpCircle, cls: 'text-amber-700 dark:text-amber-400', label: 'Nicht zugeordnet' },
      irrelevant: { Icon: MinusCircle, cls: 'text-slate-500 dark:text-slate-400', label: 'Irrelevant' },
      fehler: { Icon: AlertTriangle, cls: 'text-red-700 dark:text-red-400', label: 'Fehler' },
      neu: { Icon: Loader2, cls: 'text-slate-500', label: 'Neu' },
    };
    const v = map[s] ?? map.neu;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${v.cls}`}>
        <v.Icon className="w-3.5 h-3.5" /> {v.label}
      </span>
    );
  };

  return (
    <div className={cardClasses} translate="no">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <Inbox className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div className="min-w-0">
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Posteingang</h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Automatisch abgerufene Mails — Typ &amp; Kundenzuordnung (read-only)
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handlePoll}
          disabled={polling}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex-shrink-0 disabled:opacity-60"
          title="Postfach jetzt auf neue Mails prüfen"
        >
          <span className="inline-flex">
            {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </span>
          <span>{polling ? 'Prüfe…' : 'Jetzt prüfen'}</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">{error}</div>
      )}
      {pollMsg && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm">{pollMsg}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>
      ) : rows.length === 0 ? (
        <div className={`text-sm rounded-lg border border-dashed p-6 text-center ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
          Noch keine Mails verarbeitet.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((m) => (
            <div key={m.id} className={`rounded-lg border p-3 ${darkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50/60'}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {m.subject || '(kein Betreff)'}
                </span>
                {statusBadge(m.status)}
              </div>
              <div className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {(m.sentAt || m.createdAt) && (
                  <>🕒 {new Date(m.sentAt || m.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · </>
                )}
                {m.fromAddress || '—'}
                {m.detectedType && <> · Typ: {TYPE_LABEL[m.detectedType] ?? m.detectedType}</>}
                {m.matchedCustomerId != null && <> · Kunde #{m.matchedCustomerId}</>}
                {m.confidence != null && <> · {Math.round(m.confidence * 100)}%</>}
                {m.vorgangsnummer && <> · Vorgang: {m.vorgangsnummer}</>}
                {m.documentStatus === 'scanned' && <> · 📎 Dokument erfasst</>}
                {m.documentStatus === 'link_only' && <> · 🔗 nur Link</>}
                {m.documentStatus === 'failed' && <> · ⚠️ Dok. fehlgeschlagen</>}
                {m.vorgangId != null && <> · ✅ am Vorgang #{m.vorgangId}</>}
              </div>
              {m.status === 'fehler' && m.error && (
                <div className="text-xs mt-1 text-red-600 dark:text-red-400 break-all">{m.error}</div>
              )}
              <button
                onClick={() => toggleMail(m.id)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                {bodyLoading === m.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Mail className="w-3.5 h-3.5" />}
                {openMail === m.id ? 'Original-Mail ausblenden' : 'Original-Mail anzeigen'}
              </button>
              {openMail === m.id && bodyLoading !== m.id && (
                <pre className={`mt-1.5 text-xs whitespace-pre-wrap rounded-md p-2 max-h-64 overflow-y-auto ${darkMode ? 'text-slate-300 bg-slate-900/60' : 'text-slate-600 bg-white'}`}>
                  {bodies[m.id] ? linkifyMail(bodies[m.id] as string) : 'Kein Mailtext gespeichert.'}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
